import React, { useEffect } from 'react';
import { FormProvider, useFormContext } from 'react-hook-form';

import styles from './Operational.module.scss';
import Button from '../../../../components/button/button';
import FormCheckbox from '../../../../components/formCheckbox/FormCheckbox';
import FormNewCheckbox from '../../../../components/formCheckbox/FormNewCheckbox';
import Portal from '../../../../components/portal/Portal';
import StepperNavigation from '../../../../components/stepper/StepperNavigation';
import SectionContainer from '../../../../components/template/SectionContainer/SectionContainer';
import Subtitle from '../../../../components/template/Subtitle/Subtitle';
import Title from '../../../../components/template/Title/Title';
import { QuestionForm } from '../../../../models/questionModel';
import useFormStore from '../../../../store/FormStore';
import useQuestionStore from '../../../../store/QuestionStore';
import useStepperStore from '../../../../store/StepperStore';
import MainQuestion from '../MainQuestion/MainQuestion';

interface OperationalProps {}

const Operational: React.FC<OperationalProps> = ({}) => {
  const { setQuestion } = useQuestionStore();
  const { setStepper } = useStepperStore();
  const { setForm } = useFormStore();

  //#region Form
  const formMethods = useFormContext();
  const {
    getValues,
    reset,
    watch,
    formState: { isValid },
  } = formMethods;
  const { operational } = watch();
  //#endregion

  useEffect(() => {
    reset(getValues());
  }, [operational?.compulsory]);

  //#region Handler

  const getQuesWithMarks = (
    impactTitle: 'Governance' | 'Cash Flows' | 'Assets'
  ) => {
    const selectedImpact = OperationalData.impactQuestions.find(
      (impact) => impact.title === impactTitle
    );

    const getQuesWithMarksBySection = (
      sectionId:
        | 'operational.governance'
        | 'operational.cashflows'
        | 'operational.assets'
    ) => {
      return selectedImpact?.questions.map((ques, quesIdx) => {
        let selectedCheckbox = ques.checkbox.find((check) => {
          return check.marks == getValues(sectionId)[quesIdx];
        });
        let selectedAnswer = selectedCheckbox?.isInput
          ? getValues(`${ques.id}_answer`)
          : selectedCheckbox?.label;
        return {
          question: ques.question,
          marks: selectedCheckbox?.marks || 0,
          answer: selectedAnswer || '',
        };
      });
    };

    switch (impactTitle) {
      case 'Governance':
        return getQuesWithMarksBySection('operational.governance');
      case 'Cash Flows':
        return getQuesWithMarksBySection('operational.cashflows');
      case 'Assets':
        return getQuesWithMarksBySection('operational.assets');
    }
  };

  const onHandleNavigation = (nav: 'next' | 'prev') => {
    if (operational?.compulsory == 'Yes') {
      const compulsoryQuestions = OperationalData.compulsoryQuestions.map(
        (comp) => {
          return {
            answer: getValues('operational.compulsory'),
            question: comp.question,
            questionNotes: [],
            answerNotes: [],
          };
        }
      );

      const impactQuestions = OperationalData.impactQuestions.flatMap(
        (imp) =>
          getQuesWithMarks(imp.title as any)?.map((q) => ({
            ...q,
            answerNotes: [],
            questionNotes: [],
          })) || []
      );

      const probabilityQuestions = OperationalData.probabilityQuestions.map(
        (comp) => {
          return {
            answer:
              comp.inputs.find(
                (inp) =>
                  inp.value == Number(getValues('operational.probability'))
              )?.label || '',
            question: comp.question,
            questionNotes: [],
            answerNotes: [],
            marks: Number(getValues('operational.probability')),
          };
        }
      );

      const finalOperationalData = new QuestionForm(
        OperationalData.title,
        OperationalData.description,
        compulsoryQuestions,
        impactQuestions,
        probabilityQuestions
      );

      setQuestion({ operational: finalOperationalData });
    }

    setForm({ ...getValues() });

    setStepper(
      nav,
      undefined,
      operational?.compulsory == ''
        ? undefined
        : operational?.compulsory == 'Yes'
    );
  };

  const generateRandomData = (data: any) => {
    const pickOneOrFive = () => (Math.random() > 0.5 ? 5 : 1);

    const getSection = (title: string) =>
      data
        .find((s: any) => s.title === title)
        ?.questions.filter((q: any) => !q.isInput) // exclude isInput === true
        .map(() => pickOneOrFive()) || [];

    return {
      compulsory: Math.random() > 0.5 ? 'Yes' : 'Not applicable',
      probability: pickOneOrFive(),
      governance: getSection('Governance'),
      cashflows: getSection('Cash Flows'),
      assets: getSection('Assets'),
    };
  };

  //#endregion

  const OperationalData = {
    title: 'Operational Risk',
    description:
      'Operational risk is the prospect of loss resulting from inadequate or failed procedures, human error, systems or policies.',
    compulsoryQuestions: [
      {
        id: 'operational.compulsory',
        question: 'Is your Company exposed to operational risk?',
        inputs: [
          { value: 'Yes', label: 'Yes' },
          { value: 'Not applicable', label: 'Not applicable' },
        ],
      },
    ],
    probabilityQuestions: [
      {
        id: 'operational.probability',
        question: 'How frequent is the Company facing the risk?',
        inputs: [
          { value: 1, label: 'Improbable' },
          { value: 2, label: 'Remote' },
          { value: 3, label: 'Fairly Likely' },
          { value: 4, label: 'Probable' },
          { value: 5, label: 'Very Likely' },
        ],
      },
    ],
    impactQuestions: [
      {
        title: 'Governance',
        questions: [
          {
            id: 'operational.governance.0',
            question:
              'Is the company in compliance with the Companies Act 2016 requirements <br /> (e.g., annual returns, financial statements)?',
            checkbox: [
              {
                label:
                  'Yes, all statutory filings are submitted on time with no outstanding compliance issues.',
                marks: 5,
              },
              {
                label:
                  'No, Some filings are delayed or incomplete, exposing the company to penalties.',
                marks: 1,
              },
              {
                label: 'Others',
                marks: 3,
                isInput: true,
              },
            ],
          },
          {
            id: 'operational.governance.1',
            question:
              'Please indicate the status of training in your department?',
            checkbox: [
              {
                label:
                  'Training is provided at least annually to all applicable employees, and there are discussions with employees to confirm that training is adequate.',
                marks: 5,
              },
              {
                label:
                  'Some training is being provided to applicable employees; however, additional training is needed.',
                marks: 3,
              },
              {
                label:
                  'Very little training is being provided, and the adequacy of the training is not effective.',
                marks: 1,
              },
            ],
          },
          {
            id: 'operational.governance.2',
            question:
              'Are duties segregated so that any individual with access to assets, cannot initiate, authorise and record transactions on the computer?',
            checkbox: [
              {
                label: 'Yes',
                marks: 5,
              },
              {
                label: 'No',
                marks: 1,
              },
              {
                label: 'Others',
                marks: 3,
                isInput: true,
              },
            ],
          },
          {
            id: 'operational.governance.3',
            question:
              'What is the degree of confidentiality of the information produced or handled by the department?',
            checkbox: [
              {
                label:
                  'Information produced by the department is not confidential and is generally available to the public, the release of which would not result in any potential loss or embarrassment to the Company.',
                marks: 5,
              },
              {
                label:
                  'Information produced by the department is available to designated employees of the Company in connection with their jobs. Release to the public or to an unauthorized entity could result in minor financial loss or moderate embarrassment or violation of an individual’s privacy.',
                marks: 3,
              },
              {
                label:
                  'Information produced by the department requires protection against unauthorized or premature disclosure. Such disclosure could result in serious loss or embarrassment or could adversely affect the department, the Company or the subject of the information.',
                marks: 1,
              },
            ],
          },
          {
            id: 'operational.governance.4',
            question:
              'Are written contracts provided to all employees in compliance with the Employment Act?',
            checkbox: [
              {
                label:
                  'Yes, all employees receive contracts specifying terms, wages, working hours, and benefits.',
                marks: 5,
              },
              {
                label:
                  'No, some employees are working without formal written contracts.',
                marks: 1,
              },
              {
                label: 'Others',
                marks: 3,
                isInput: true,
              },
            ],
          },
        ],
      },
      {
        title: 'Cash Flows',
        questions: [
          {
            id: 'operational.cashflows.0',
            question: 'To what extent does your department handle cash?',
            checkbox: [
              {
                label:
                  'Does not handle any cash, checks, or credit card payments.',
                marks: 5,
              },
              {
                label:
                  'There is limited activity with cash, checks or credit card receipts or potential for access to them.',
                marks: 3,
              },
              {
                label:
                  'The handling of cash, checks, and credit card payments is a major part of the department’s responsibilities.',
                marks: 1,
              },
            ],
          },
          {
            id: 'operational.cashflows.1',
            question:
              'Did the cash disbursement have a proper authorisation of transactions and segregation of duties?',
            checkbox: [
              {
                label: 'Yes',
                marks: 5,
              },
              {
                label: 'No',
                marks: 1,
              },
              {
                label: 'Others',
                marks: 3,
                isInput: true,
              },
            ],
          },
          {
            id: 'operational.cashflows.2',
            question:
              'Did the signed cheques and electronic funds transfer (EFT) payment confirmations are checked against payment summaries to ensure veracity?',
            checkbox: [
              {
                label: 'Yes',
                marks: 5,
              },
              {
                label: 'No',
                marks: 1,
              },
              {
                label: 'Others',
                marks: 3,
                isInput: true,
              },
            ],
          },
          {
            id: 'operational.cashflows.3',
            question:
              'Any minimum forecasting and planning for the immediate (e.g month) on a day to day basis that are updated daily?',
            checkbox: [
              {
                label: 'Yes',
                marks: 5,
              },
              {
                label: 'No',
                marks: 1,
              },
              {
                label: 'Others',
                marks: 3,
                isInput: true,
              },
            ],
          },
          {
            id: 'operational.cashflows.4',
            question:
              'Did the Company financial position and actual cash flow results are reviewed regularly and compared to budgeted outcome?',
            checkbox: [
              {
                label: 'Yes',
                marks: 5,
              },
              {
                label: 'No',
                marks: 1,
              },
              {
                label: 'Others',
                marks: 3,
                isInput: true,
              },
            ],
          },
        ],
      },
      {
        title: 'Assets',
        questions: [
          {
            id: 'operational.assets.0',
            question:
              'Is the property compliant with local building codes, safety, and zoning regulations?',
            checkbox: [
              {
                label:
                  'The building has up-to-date permits and complies with all regulatory requirements.',
                marks: 5,
              },
              {
                label: 'Several compliance documents are outdated or missing.',
                marks: 3,
              },
              {
                label: 'Others',
                marks: 1,
                isInput: true,
              },
            ],
          },
          {
            id: 'operational.assets.1',
            question:
              'Are insurance policies in place to cover property damage and liability?',
            checkbox: [
              {
                label:
                  'Comprehensive insurance covers fire, theft, natural disasters, and liability.',
                marks: 5,
              },
              {
                label: 'Insurance coverage is inadequate or expired.',
                marks: 1,
              },
              {
                label: 'Others',
                marks: 3,
                isInput: true,
              },
            ],
          },
          {
            id: 'operational.assets.2',
            question:
              'Do you have any departmental inventory (not fixed assets or equipment) or specialized inventory such as controlled substances, hazardous wastes, or precious metals?',
            checkbox: [
              {
                label:
                  'Inventories are valued at low dollar amounts and do not include specialized items or no inventory.',
                marks: 5,
              },
              {
                label:
                  'Inventories are at relatively moderate dollar amounts and do not include specialized items.',
                marks: 3,
              },
              {
                label:
                  'Inventories are valued at high dollar amounts or include specialized items, such as hazardous wastes. (Please indicate which.)',
                marks: 1,
              },
            ],
          },
          {
            id: 'operational.assets.3',
            question:
              'Are regular maintenance and safety inspections conducted on all machinery?',
            checkbox: [
              {
                label: 'Yes, maintenance is conducted monthly and logged.',
                marks: 5,
              },
              {
                label:
                  'Maintenance is done irregularly and inspections are only carried out when an issue is reported.',
                marks: 1,
              },
              {
                label: 'Others',
                marks: 3,
                isInput: true,
              },
            ],
          },
          {
            id: 'operational.assets.4',
            question:
              'Is there a backup plan in case of machinery or technology failure?',
            checkbox: [
              {
                label:
                  'Yes, we maintain spare units for critical machinery and a business continuity plan for essential operations.',
                marks: 5,
              },
              {
                label:
                  'No comprehensive backup plan exists; we typically respond reactively when a machine fails.',
                marks: 1,
              },
              {
                label: 'Others',
                marks: 3,
                isInput: true,
              },
            ],
          },
        ],
      },
    ],
  };

  return (
    <FormProvider {...formMethods}>
      <div className={styles.container}>
        <MainQuestion
          id="operational"
          title={OperationalData.title}
          description={OperationalData.description}
          compulsoryQuestions={OperationalData.compulsoryQuestions}
          probabilityQuestions={OperationalData.probabilityQuestions}
        />

        {operational?.compulsory && operational?.compulsory == 'Yes' && (
          <div>Operational Risk Assessment Questionnaire</div>
        )}

        {operational?.compulsory &&
          operational?.compulsory == 'Yes' &&
          OperationalData.impactQuestions.map((impact, idx) => (
            <SectionContainer key={idx}>
              <Title>{impact.title}</Title>

              {impact.questions.map((ques, idx) => (
                <div key={idx}>
                  <div>
                    {idx + 1}. {ques.question}
                  </div>
                  {ques.checkbox.map((check, index) => (
                    <FormNewCheckbox
                      key={index}
                      id={ques.id}
                      label={check.label}
                      value={check.marks}
                      required
                      textInput={{
                        id: check?.isInput ? `${ques.id}_answer` : '',
                        placeholder: check?.label,
                      }}
                    />
                  ))}
                </div>
              ))}
            </SectionContainer>
          ))}
      </div>

      <StepperNavigation
        isValid={isValid}
        onPrev={() => onHandleNavigation('prev')}
        onNext={() => onHandleNavigation('next')}
      />

      <Portal root="tester">
        <Button
          isCircle
          onClick={() => {
            reset({
              ...getValues(),
              operational: {
                ...getValues().operational,
                ...generateRandomData(OperationalData.impactQuestions),
              },
            });
          }}
        >
          Random
        </Button>
      </Portal>
    </FormProvider>
  );
};

export default Operational;
